import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, apiGet, apiPost } from '../services/api';
import { bimestreDaData } from '../utils/bimestres';
import { CONCEITOS, COR_DO_CONCEITO, chaveConceito } from '../utils/habilidades';

/**
 * Registro de Habilidades do 1º e 2º ano e da Educação Infantil — a aba Notas
 * do diário nessas séries.
 *
 * Três jeitos de ver o mesmo registro:
 * - Turma: alunos nas linhas, habilidades nas colunas, um bimestre por vez.
 *   É o jeito rápido de lançar a turma inteira.
 * - Ficha do aluno: a habilidade por extenso e os 4 bimestres lado a lado,
 *   como no formulário em papel da rede.
 * - Celular: um aluno por vez, uma habilidade por cartão, botões grandes.
 *
 * Na Educação Infantil (Matriz de Acompanhamento do Desenvolvimento Infantil)
 * o servidor manda a matriz inteira e os `campos` de experiência: a turma e o
 * celular mostram um campo por vez; a ficha mostra a matriz toda, campo a
 * campo, como no papel.
 *
 * Para lançar a turma inteira no computador há o vaivém da planilha, como na
 * aba Notas: baixar em Excel (uma aba por bimestre), preencher e importar.
 *
 * Cada clique grava na hora (POST /habilidades/lancar/). A tela muda antes da
 * resposta e volta atrás se o servidor recusar, com o motivo na tela: sem
 * isso a professora acharia que lançou e o conceito não estaria lá.
 */

const LARGURA_MENU = 300;

function useTelaEstreita() {
  const consulta = '(max-width: 767px)';
  const [estreita, setEstreita] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(consulta).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(consulta);
    if (!mq) return undefined;
    const mudou = (e) => setEstreita(e.matches);
    mq.addEventListener?.('change', mudou);
    return () => mq.removeEventListener?.('change', mudou);
  }, []);
  return estreita;
}

function Pilula({ conceito, grande = false }) {
  if (!conceito) {
    return (
      <span className={`inline-block text-center rounded-md border border-dashed border-slate-500 text-slate-500 ${grande ? 'min-w-[52px] py-1.5 text-sm' : 'min-w-[44px] py-1 text-xs'}`}>
        —
      </span>
    );
  }
  return (
    <span
      className={`inline-block text-center rounded-md font-bold text-white ${grande ? 'min-w-[52px] py-1.5 text-sm' : 'min-w-[44px] py-1 text-xs'}`}
      style={{ background: COR_DO_CONCEITO[conceito] }}
    >
      {conceito}
    </span>
  );
}

/** Popover em posição fixa, ancorado no retângulo do elemento clicado. A
 * tabela rola na horizontal, e um popover dentro dela seria cortado. */
function Popover({ ancora, onFechar, children, darkMode, largura = LARGURA_MENU }) {
  useEffect(() => {
    const fechar = () => onFechar();
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    const esc = (e) => e.key === 'Escape' && onFechar();
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
      window.removeEventListener('keydown', esc);
    };
  }, [onFechar]);

  const left = Math.max(8, Math.min(ancora.left, window.innerWidth - largura - 8));
  const embaixo = ancora.bottom + 320 < window.innerHeight;
  const posicao = embaixo
    ? { top: ancora.bottom + 6 }
    : { bottom: window.innerHeight - ancora.top + 6 };
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onFechar} aria-hidden="true" />
      <div
        role="dialog"
        className={`fixed z-50 rounded-xl p-2 shadow-2xl border ${darkMode
          ? 'bg-[#0b1222] border-slate-600 text-slate-200'
          : 'bg-white border-slate-200 text-slate-700'
        }`}
        style={{ left, width: largura, ...posicao }}
      >
        {children}
      </div>
    </>
  );
}

function RegistroHabilidades({
  turmaId,
  disciplinaId,
  componentes,
  componenteSelecionado,
  onSelecionarComponente,
  anoLetivo,
  onAnoLetivo,
  canEdit,
  darkMode,
  downloadPdf,
  baixandoPdf,
}) {
  const estreita = useTelaEstreita();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erroCarga, setErroCarga] = useState('');
  const [conceitos, setConceitos] = useState({});
  const [bimestre, setBimestre] = useState(() =>
    bimestreDaData(new Date().toISOString().slice(0, 10)));
  const [modo, setModo] = useState('turma');
  const [alunoIdx, setAlunoIdx] = useState(0);
  const [menu, setMenu] = useState(null); // { aluno, habilidade, ancora }
  const [balao, setBalao] = useState(null); // { habilidade, ancora }
  const [salvando, setSalvando] = useState(0);
  const [erroSalvar, setErroSalvar] = useState('');
  const [campo, setCampo] = useState('');
  // Planilha em Excel: baixar, preencher, importar. `recarga` relê o
  // registro depois da importação.
  const [baixandoExcel, setBaixandoExcel] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);
  const [mostrarComoImportar, setMostrarComoImportar] = useState(false);
  const [recarga, setRecarga] = useState(0);

  // ============ CARGA ============
  useEffect(() => {
    if (!disciplinaId) return undefined;
    let cancelado = false;
    setCarregando(true);
    setErroCarga('');
    apiGet('/habilidades/turma/', {
      id_class: turmaId, id_subject: disciplinaId, school_year: anoLetivo,
    })
      .then((resp) => {
        if (cancelado) return;
        setDados(resp);
        const mapa = {};
        (resp.conceitos || []).forEach((c) => {
          mapa[chaveConceito(c.id_student, c.id_skill, c.bimestre)] = c.conceito;
        });
        setConceitos(mapa);
        setAlunoIdx((i) => Math.min(i, Math.max(0, (resp.alunos || []).length - 1)));
      })
      .catch((err) => {
        if (cancelado) return;
        setDados(null);
        setConceitos({});
        setErroCarga(err?.message || 'Não foi possível carregar as habilidades.');
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => { cancelado = true; };
  }, [turmaId, disciplinaId, anoLetivo, recarga]);

  // Outra disciplina ou outro ano: o aviso da última importação não vale mais.
  useEffect(() => { setResultadoImport(null); }, [disciplinaId, anoLetivo]);

  const todasHabilidades = useMemo(() => dados?.habilidades || [], [dados]);
  const campos = useMemo(() => dados?.campos || [], [dados]);
  const infantil = campos.length > 0;
  const campoAtual = campos.includes(campo) ? campo : (campos[0] || '');
  const habilidadesDoCampo = useMemo(
    () => (infantil ? todasHabilidades.filter((h) => h.componente === campoAtual) : todasHabilidades),
    [infantil, todasHabilidades, campoAtual],
  );
  const alunos = useMemo(() => dados?.alunos || [], [dados]);
  const legenda = dados?.legenda || [];
  const podeLancar = !!(canEdit && dados?.pode_lancar);
  const nomeComponente = infantil
    ? campoAtual
    : dados?.componente || dados?.disciplina?.subject_name || '';
  const aluno = alunos[alunoIdx];

  const conceitoDe = useCallback(
    (idAluno, idHabilidade, b = bimestre) => conceitos[chaveConceito(idAluno, idHabilidade, b)] || null,
    [conceitos, bimestre],
  );

  // ============ GRAVAÇÃO ============
  const lancar = useCallback(async (itens) => {
    if (!podeLancar || itens.length === 0) return;
    const antes = {};
    const depois = {};
    itens.forEach((it) => {
      const k = chaveConceito(it.id_student, it.id_skill, it.bimestre);
      antes[k] = conceitos[k];
      depois[k] = it.conceito;
    });
    const aplicar = (mudancas) => setConceitos((atual) => {
      const novo = { ...atual };
      Object.entries(mudancas).forEach(([k, v]) => {
        if (v) novo[k] = v; else delete novo[k];
      });
      return novo;
    });
    aplicar(depois);
    setErroSalvar('');
    setSalvando((n) => n + 1);
    try {
      await apiPost('/habilidades/lancar/', {
        id_class: turmaId,
        id_subject: disciplinaId,
        school_year: anoLetivo,
        itens,
      });
    } catch (err) {
      aplicar(antes);
      setErroSalvar(err?.message || 'Não foi possível salvar. Tente de novo.');
    } finally {
      setSalvando((n) => n - 1);
    }
  }, [podeLancar, conceitos, turmaId, disciplinaId, anoLetivo]);

  const marcar = (idAluno, idHabilidade, b, conceito) => lancar([{
    id_student: idAluno, id_skill: idHabilidade, bimestre: b, conceito,
  }]);

  // Clicar no conceito que já está marcado desmarca.
  const alternar = (idAluno, idHabilidade, b, conceito) => {
    const atual = conceitoDe(idAluno, idHabilidade, b);
    marcar(idAluno, idHabilidade, b, atual === conceito ? null : conceito);
  };

  const preencherColuna = (habilidade, conceito) => {
    const itens = alunos
      .filter((a) => !conceitoDe(a.id_student, habilidade.id))
      .map((a) => ({ id_student: a.id_student, id_skill: habilidade.id, bimestre, conceito }));
    lancar(itens);
    setBalao(null);
  };

  const baixar = (idAluno) => {
    const params = { id_class: turmaId, id_subject: disciplinaId, school_year: anoLetivo };
    if (idAluno) params.id_student = idAluno;
    downloadPdf('/habilidades/pdf/', params, 'registro_habilidades.pdf', idAluno ? 'hab_aluno' : 'hab_turma');
  };

  // ============ PLANILHA (EXCEL) ============
  const baixarExcel = async () => {
    if (!disciplinaId) return;
    setBaixandoExcel(true);
    try {
      const params = new URLSearchParams({
        id_class: turmaId,
        id_subject: disciplinaId,
        school_year: anoLetivo,
        bimestre,
      });
      const response = await fetch(`${API_BASE_URL}/habilidades/planilha/?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const erro = await response.json().catch(() => ({}));
        throw new Error(erro.error || erro.detail || 'Erro ao baixar a planilha');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers.get('Content-Disposition');
      const a = document.createElement('a');
      a.href = url;
      a.download = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `habilidades_${anoLetivo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar planilha de habilidades:', err);
      setResultadoImport({ success: false, error: err.message });
    } finally {
      setBaixandoExcel(false);
    }
  };

  const importarExcel = async (event) => {
    const file = event.target.files[0];
    if (!file || !disciplinaId) return;
    setImportando(true);
    setResultadoImport(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id_class', turmaId);
      formData.append('id_subject', disciplinaId);
      formData.append('school_year', anoLetivo);
      const response = await fetch(`${API_BASE_URL}/habilidades/importar-planilha/`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Erro ao importar planilha');
      }
      setResultadoImport(data);
      setRecarga((n) => n + 1);
    } catch (err) {
      console.error('Erro ao importar habilidades:', err);
      setResultadoImport({ success: false, error: err.message });
    } finally {
      setImportando(false);
      event.target.value = '';
    }
  };

  // A ficha do Infantil é a matriz inteira; as outras visões, um campo por vez.
  const naFichaDoInfantil = infantil && modo === 'ficha' && !estreita;
  const habilidades = naFichaDoInfantil ? todasHabilidades : habilidadesDoCampo;

  // ============ ESTILOS ============
  const texto = darkMode ? 'text-slate-200' : 'text-slate-700';
  const textoFraco = darkMode ? 'text-slate-400' : 'text-slate-500';
  const borda = darkMode ? 'border-slate-700' : 'border-slate-200';
  const chip = (ativo) => `px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${ativo
    ? 'text-white border-transparent shadow'
    : darkMode
      ? 'border-slate-600 text-slate-300 hover:border-slate-500'
      : 'border-slate-200 text-slate-600 hover:border-slate-300'
  }`;
  const fundoAtivo = { background: 'linear-gradient(135deg, #0974BA, #2E4974)' };
  const botaoSecundario = `px-3 py-1.5 rounded-lg text-sm font-medium border ${darkMode
    ? 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'
    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
  } disabled:opacity-50 disabled:cursor-not-allowed`;
  const botaoAcao = (desabilitado, gradiente) => `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${desabilitado
    ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
    : `text-white bg-gradient-to-r ${gradiente}`
  }`;
  const botaoPdf = (desabilitado) => `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${desabilitado
    ? 'bg-slate-500 text-white cursor-not-allowed opacity-60'
    : 'text-white bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
  }`;

  // ============ PEDAÇOS ============
  const seletorBimestre = (compacto = false) => (
    <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-slate-600' : 'border-slate-200'} ${compacto ? 'w-full' : ''}`}>
      {[1, 2, 3, 4].map((b) => (
        <button
          key={b}
          type="button"
          onClick={() => setBimestre(b)}
          className={`px-3 py-1.5 text-sm ${compacto ? 'flex-1' : ''} ${b > 1 ? (darkMode ? 'border-l border-slate-600' : 'border-l border-slate-200') : ''} ${bimestre === b
            ? 'bg-[#0974BA] text-white font-semibold'
            : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          {compacto ? `${b}º Bim` : `${b}º Bimestre`}
        </button>
      ))}
    </div>
  );

  const navegacaoAluno = (
    <div className={`flex items-center justify-between gap-3 rounded-xl p-2 ${darkMode ? 'bg-slate-900/40' : 'bg-slate-50'}`}>
      <button type="button" className={botaoSecundario} disabled={alunoIdx === 0}
        onClick={() => setAlunoIdx((i) => Math.max(0, i - 1))} aria-label="Aluno anterior">
        ‹ <span className="hidden sm:inline">Anterior</span>
      </button>
      <div className="text-center min-w-0">
        <select
          value={alunoIdx}
          onChange={(e) => setAlunoIdx(Number(e.target.value))}
          className={`max-w-full text-base sm:text-lg font-semibold bg-transparent text-center truncate focus:outline-none ${texto}`}
          aria-label="Escolher aluno"
        >
          {alunos.map((a, i) => (
            <option key={a.id_student} value={i} className="text-slate-800">{a.student_name}</option>
          ))}
        </select>
        <div className={`text-xs ${textoFraco}`}>
          aluno {alunoIdx + 1} de {alunos.length}
          {aluno && ` · ${habilidades.filter((h) => conceitoDe(aluno.id_student, h.id)).length}/${habilidades.length} lançadas no ${bimestre}º bim`}
        </div>
      </div>
      <button type="button" className={botaoSecundario} disabled={alunoIdx >= alunos.length - 1}
        onClick={() => setAlunoIdx((i) => Math.min(alunos.length - 1, i + 1))} aria-label="Próximo aluno">
        <span className="hidden sm:inline">Próximo</span> ›
      </button>
    </div>
  );

  const legendaCompleta = (
    <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
      {legenda.map((l) => (
        <div key={l.conceito} className={`flex items-center gap-2 text-xs ${textoFraco}`}>
          <Pilula conceito={l.conceito} />
          <span><b className={texto}>{l.nome}</b> — {l.descricao}</span>
        </div>
      ))}
    </div>
  );

  // ============ VISÕES ============
  const visaoTurma = () => (
    <div className={`mt-4 overflow-x-auto rounded-lg border ${borda}`}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className={darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}>
            <th className={`sticky left-0 z-10 text-left px-3 py-2 font-semibold min-w-[180px] ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
              Aluno
            </th>
            {habilidades.map((h) => (
              <th key={h.id} className={`px-2 py-2 align-top font-normal min-w-[112px] max-w-[140px] border-l ${borda}`}>
                <button
                  type="button"
                  onClick={(e) => setBalao({ habilidade: h, ancora: e.currentTarget.getBoundingClientRect() })}
                  className="text-left w-full group"
                  title={`${h.codigo} — ${h.descricao}`}
                >
                  <span className="block text-xs font-bold text-[#3B9AE1] underline decoration-dotted underline-offset-2 group-hover:text-[#5fb0ee]">
                    {h.codigo}
                  </span>
                  <span className={`text-[11px] leading-tight mt-1 line-clamp-2 ${textoFraco}`}>
                    {h.descricao}
                  </span>
                </button>
              </th>
            ))}
            <th className={`px-3 py-2 font-semibold border-l ${borda} ${texto}`}>Resumo</th>
          </tr>
        </thead>
        <tbody>
          {alunos.map((a, i) => {
            const consolidadas = habilidades.filter((h) => conceitoDe(a.id_student, h.id) === 'HC').length;
            const lancadas = habilidades.filter((h) => conceitoDe(a.id_student, h.id)).length;
            return (
              <tr key={a.id_student} className={`border-t ${borda} ${i % 2 ? (darkMode ? 'bg-slate-800/30' : 'bg-slate-50/60') : ''}`}>
                <td className={`sticky left-0 z-10 px-3 py-2 whitespace-nowrap ${texto} ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
                  <button type="button" className="text-left hover:underline"
                    onClick={() => { setAlunoIdx(i); setModo('ficha'); }}
                    title="Abrir a ficha do aluno">
                    {a.student_name}
                  </button>
                </td>
                {habilidades.map((h) => {
                  const c = conceitoDe(a.id_student, h.id);
                  return (
                    <td key={h.id} className={`px-1 py-1.5 text-center border-l ${borda}`}>
                      <button
                        type="button"
                        disabled={!podeLancar}
                        onClick={(e) => setMenu({ aluno: a, habilidade: h, ancora: e.currentTarget.getBoundingClientRect() })}
                        className={`rounded-md p-0.5 ${podeLancar ? 'hover:ring-2 hover:ring-[#0974BA]/60 cursor-pointer' : 'cursor-default'}`}
                        aria-label={`${a.student_name}, ${h.codigo}: ${c || 'sem conceito'}`}
                      >
                        <Pilula conceito={c} />
                      </button>
                    </td>
                  );
                })}
                <td className={`px-3 py-1.5 border-l ${borda} min-w-[120px]`}>
                  <div className={`text-xs ${texto}`}>{consolidadas}/{habilidades.length} consolidadas</div>
                  <div className={`h-1.5 rounded mt-1 overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div className="h-full bg-[#16A34A]" style={{ width: `${habilidades.length ? (100 * consolidadas) / habilidades.length : 0}%` }} />
                  </div>
                  <div className={`text-[11px] mt-0.5 ${textoFraco}`}>{lancadas}/{habilidades.length} lançadas</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const visaoFicha = () => (
    <div className="mt-4">
      {navegacaoAluno}
      <div className={`mt-3 overflow-x-auto rounded-lg border ${borda}`}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className={darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}>
              <th className={`text-left px-3 py-2 font-semibold min-w-[320px] ${texto}`}>Habilidades</th>
              {[1, 2, 3, 4].map((b) => (
                <th key={b} className={`px-2 py-2 font-semibold border-l ${borda} ${texto} ${bimestre === b ? (darkMode ? 'bg-[#1d3a5f]' : 'bg-blue-50') : ''}`}>
                  <button type="button" onClick={() => setBimestre(b)}>
                    {b}º Bimestre{bimestre === b ? ' (atual)' : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aluno && habilidades.map((h, i) => (
              <React.Fragment key={h.id}>
                {infantil && h.componente !== habilidades[i - 1]?.componente && (
                  <tr className={`border-t ${borda} ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                    <td colSpan={5} className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#3B9AE1]">
                      Campo de Experiência {campos.indexOf(h.componente) + 1}: {h.componente}
                    </td>
                  </tr>
                )}
                <tr className={`border-t ${borda}`}>
                  <td className={`px-3 py-2 leading-snug ${texto}`}>
                    <span className="font-bold text-[#3B9AE1]">({h.codigo})</span> {h.descricao}
                  </td>
                  {[1, 2, 3, 4].map((b) => {
                    const atual = conceitoDe(aluno.id_student, h.id, b);
                    return (
                      <td key={b} className={`px-2 py-2 text-center border-l ${borda} ${bimestre === b ? (darkMode ? 'bg-slate-700/30' : 'bg-blue-50/50') : ''}`}>
                        <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-slate-600' : 'border-slate-300'}`}>
                          {CONCEITOS.map((c, j) => (
                            <button
                              key={c}
                              type="button"
                              disabled={!podeLancar}
                              onClick={() => alternar(aluno.id_student, h.id, b, c)}
                              className={`px-2 py-1 text-xs font-semibold ${j ? (darkMode ? 'border-l border-slate-600' : 'border-l border-slate-300') : ''} ${atual === c
                                ? 'text-white'
                                : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
                              } disabled:cursor-default`}
                              style={atual === c ? { background: COR_DO_CONCEITO[c] } : {}}
                              aria-pressed={atual === c}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const visaoCelular = () => (
    <div className="mt-3 space-y-3">
      {seletorBimestre(true)}
      {navegacaoAluno}
      {aluno && habilidades.map((h) => {
        const atual = conceitoDe(aluno.id_student, h.id);
        return (
          <div key={h.id} className={`rounded-xl border p-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`text-sm leading-snug ${texto}`}>
              <span className="block font-bold text-[#3B9AE1]">{h.codigo}</span>
              {h.descricao}
            </div>
            <div className="flex gap-1.5 mt-3">
              {CONCEITOS.map((c) => (
                <button
                  key={c}
                  type="button"
                  disabled={!podeLancar}
                  onClick={() => alternar(aluno.id_student, h.id, bimestre, c)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-bold border ${atual === c
                    ? 'text-white border-transparent'
                    : darkMode ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'
                  } disabled:cursor-default`}
                  style={atual === c ? { background: COR_DO_CONCEITO[c] } : {}}
                  aria-pressed={atual === c}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <p className={`text-xs ${textoFraco}`}>
        HND não desenvolvida · HDI em desenvolvimento inicial · HD em desenvolvimento · HC consolidada.
        Toque de novo no conceito marcado para desmarcar.
      </p>
    </div>
  );

  // ============ TELA ============
  const naFicha = modo === 'ficha' && !estreita;
  return (
    <div className={`rounded-xl p-4 sm:p-5 ${darkMode
      ? 'bg-slate-800/50 border border-slate-700/10'
      : 'bg-white/80 border border-slate-200 shadow-sm'
    }`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className={`text-sm font-semibold uppercase tracking-wider ${textoFraco}`}>
            {naFicha
              ? `Ficha do aluno — ${infantil ? 'Matriz do Desenvolvimento Infantil' : nomeComponente}`
              : infantil ? 'Matriz de Acompanhamento do Desenvolvimento Infantil' : 'Registro de Habilidades'}
            {dados?.serie ? ` · ${dados.serie}` : ''}
          </h3>
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {naFicha
              ? infantil
                ? 'A matriz do papel: os cinco Campos de Experiência e os 4 bimestres lado a lado.'
                : 'O mesmo registro do papel: habilidade por extenso e os 4 bimestres lado a lado.'
              : 'Um conceito por habilidade em cada bimestre. Toque no código para ler a habilidade inteira.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className={`text-xs ${textoFraco}`}>Ano letivo:</label>
          <input
            type="number"
            value={anoLetivo}
            onChange={(e) => onAnoLetivo(parseInt(e.target.value, 10) || new Date().getFullYear())}
            className={`w-24 px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
              ? 'bg-slate-700 border-slate-600 text-slate-200'
              : 'bg-white border-slate-200 text-slate-700'
            }`}
          />
          {!estreita && (
            <button type="button" className={botaoSecundario}
              disabled={!todasHabilidades.length || !alunos.length}
              onClick={() => setModo(naFicha ? 'turma' : 'ficha')}>
              {naFicha ? '← Voltar para a turma' : 'Ficha do aluno'}
            </button>
          )}
          {(naFicha || estreita) && aluno && (
            <button type="button" onClick={() => baixar(aluno.id_student)}
              disabled={baixandoPdf === 'hab_aluno'} className={botaoPdf(baixandoPdf === 'hab_aluno')}>
              {baixandoPdf === 'hab_aluno' ? 'Gerando...' : 'PDF do aluno'}
            </button>
          )}
          <button type="button" onClick={() => baixar(null)}
            disabled={!dados || !alunos.length || baixandoPdf === 'hab_turma'}
            className={botaoPdf(!dados || !alunos.length || baixandoPdf === 'hab_turma')}
            title="Uma página por aluno, no layout do formulário em papel">
            {baixandoPdf === 'hab_turma' ? 'Gerando...' : 'PDF da turma'}
          </button>
          <button type="button" onClick={baixarExcel}
            disabled={!dados || !alunos.length || !todasHabilidades.length || baixandoExcel}
            className={botaoAcao(
              !dados || !alunos.length || !todasHabilidades.length || baixandoExcel,
              'from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25',
            )}
            title="Excel com os alunos nas linhas, uma habilidade por coluna e uma aba por bimestre">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {baixandoExcel ? 'Baixando...' : 'Baixar em Excel'}
          </button>
          {/* Abre o passo a passo, como na aba Notas: quem nunca baixou a
              planilha não sabe qual arquivo enviar. */}
          {podeLancar && (
            <button type="button" onClick={() => setMostrarComoImportar(true)}
              disabled={!alunos.length || !todasHabilidades.length || importando}
              className={botaoAcao(
                !alunos.length || !todasHabilidades.length || importando,
                'from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25',
              )}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {importando ? 'Importando...' : 'Importar planilha preenchida'}
            </button>
          )}
        </div>
      </div>

      {resultadoImport && (
        <div className={`mt-4 p-3 rounded-lg text-sm flex justify-between gap-3 ${resultadoImport.success
          ? 'bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]'
          : 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
        }`}>
          {resultadoImport.success ? (
            <div>
              <p className="font-medium">Importação realizada com sucesso!</p>
              <p className="text-xs mt-1">
                {resultadoImport.rows_processed} alunos lidos · {resultadoImport.conceitos} conceitos gravados
              </p>
              {resultadoImport.errors && resultadoImport.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-[#F9B047]">Não entraram:</p>
                  {resultadoImport.errors.slice(0, 5).map((err) => (
                    <p key={err} className="text-xs text-[#F9B047]">{err}</p>
                  ))}
                  {resultadoImport.errors.length > 5 && (
                    <p className="text-xs text-[#F9B047]">...e mais {resultadoImport.errors.length - 5} avisos</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p>{resultadoImport.error || 'Erro ao importar planilha.'}</p>
          )}
          <button type="button" onClick={() => setResultadoImport(null)} aria-label="Fechar aviso"
            className="self-start">✕</button>
        </div>
      )}

      {mostrarComoImportar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg max-h-full overflow-y-auto rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'}`}>
            <h2 className={`text-xl font-semibold mb-1 ${texto}`}>
              {infantil ? 'Importar planilha da matriz' : 'Importar planilha de habilidades'}
            </h2>
            <p className={`text-sm mb-5 ${textoFraco}`}>
              Serve para lançar os conceitos da turma de uma vez, no computador,
              sem clicar célula por célula. São três passos.
            </p>

            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">1</span>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${texto}`}>
                    {infantil ? 'Baixe a planilha da matriz' : 'Baixe a planilha deste componente'}
                  </p>
                  <p className={`text-xs mt-0.5 ${textoFraco}`}>
                    Ela vem com os alunos nas linhas, uma habilidade por coluna,
                    uma aba para cada bimestre e os conceitos de {anoLetivo} que
                    já estão lançados. É este arquivo que você vai devolver.
                  </p>
                  <button type="button" onClick={baixarExcel} disabled={baixandoExcel}
                    className={`mt-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white ${
                      baixandoExcel ? 'bg-slate-500' : 'bg-[#0974BA] hover:bg-[#0974BA]/80'
                    }`}>
                    {baixandoExcel ? 'Baixando...' : 'Baixar em Excel'}
                  </button>
                </div>
              </li>

              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0974BA] text-white text-xs font-bold flex items-center justify-center">2</span>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${texto}`}>
                    Escolha o conceito de cada habilidade
                  </p>
                  <p className={`text-xs mt-0.5 ${textoFraco}`}>
                    Em cada célula, <b>HND</b>, <b>HDI</b>, <b>HD</b> ou <b>HC</b> (a
                    célula tem a lista para escolher). Cada bimestre fica numa aba
                    — veja as abas no rodapé do Excel. A habilidade por extenso
                    está na aba <b>Habilidades</b> e no comentário de cada código.
                    Célula em branco fica como está — não apaga o que já foi lançado.
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Não mexa nos nomes, na matrícula, nos códigos nem no
                    cabeçalho: é por eles que o sistema reconhece cada aluno e
                    cada habilidade.
                  </p>
                </div>
              </li>

              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#13983D] text-white text-xs font-bold flex items-center justify-center">3</span>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${texto}`}>
                    Envie o arquivo salvo
                  </p>
                  <p className={`text-xs mt-0.5 ${textoFraco}`}>
                    O mesmo arquivo que você baixou, agora preenchido (.xlsx).
                    Os conceitos da tela atualizam na hora.
                  </p>
                  <label className={`inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer text-white ${
                    importando ? 'bg-slate-500 cursor-not-allowed' : 'bg-[#13983D] hover:bg-[#13983D]/80'
                  }`}>
                    {importando ? 'Importando...' : 'Escolher arquivo preenchido'}
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => { setMostrarComoImportar(false); importarExcel(e); }}
                      disabled={importando}
                      className="hidden"
                    />
                  </label>
                </div>
              </li>
            </ol>

            <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
              <button type="button" onClick={() => setMostrarComoImportar(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {componentes && componentes.length > 0 && (
        <div className="mt-4">
          <p className={`text-xs mb-2 ${textoFraco}`}>
            Componente curricular — a lista é a da coordenação, no cadastro da turma.
          </p>
          <div className="flex flex-wrap gap-2">
            {componentes.map((c) => {
              const ativo = String(componenteSelecionado) === String(c.id);
              return (
                <button key={c.id} type="button" onClick={() => onSelecionarComponente(String(c.id))}
                  className={chip(ativo)} style={ativo ? fundoAtivo : {}}>
                  {c.subject_name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {infantil && !naFicha && (
        <div className="mt-4">
          <p className={`text-xs mb-2 ${textoFraco}`}>Campo de Experiência</p>
          <div className="flex flex-wrap gap-2">
            {campos.map((c, i) => {
              const ativo = c === campoAtual;
              return (
                <button key={c} type="button" onClick={() => setCampo(c)}
                  className={chip(ativo)} style={ativo ? fundoAtivo : {}}>
                  {i + 1}. {c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!estreita && !naFicha && (
        <div className="mt-4">
          <p className={`text-xs mb-2 ${textoFraco}`}>Bimestre</p>
          {seletorBimestre()}
        </div>
      )}

      {!estreita && legendaCompleta}

      {salvando > 0 && <p className={`text-xs mt-3 ${textoFraco}`}>Salvando…</p>}
      {erroSalvar && (
        <div className="mt-3 rounded-lg border border-[#E83A3C]/40 bg-[#E83A3C]/10 px-3 py-2 text-sm text-[#E83A3C] flex justify-between gap-3">
          <span>{erroSalvar} O conceito não foi gravado.</span>
          <button type="button" onClick={() => setErroSalvar('')} aria-label="Fechar aviso">✕</button>
        </div>
      )}
      {!podeLancar && dados && (
        <p className={`text-xs mt-3 ${textoFraco}`}>
          {infantil
            ? 'Somente leitura: quem preenche a matriz é a professora polivalente da turma.'
            : 'Somente leitura: quem lança é o professor da disciplina.'}
        </p>
      )}

      {!disciplinaId ? (
        <p className={`text-center py-10 ${textoFraco}`}>Selecione uma disciplina para lançar as habilidades.</p>
      ) : carregando && !dados ? (
        <p className={`text-center py-10 ${textoFraco}`}>Carregando habilidades…</p>
      ) : erroCarga ? (
        <p className="text-center py-10 text-[#E83A3C]">{erroCarga}</p>
      ) : !habilidades.length ? (
        <p className={`text-center py-10 ${textoFraco}`}>
          Nenhuma habilidade cadastrada para {nomeComponente || 'a matriz'} no {dados?.serie}.
        </p>
      ) : !alunos.length ? (
        <p className={`text-center py-10 ${textoFraco}`}>Nenhum aluno nesta turma.</p>
      ) : estreita ? visaoCelular() : naFicha ? visaoFicha() : visaoTurma()}

      {menu && (
        <Popover ancora={menu.ancora} onFechar={() => setMenu(null)} darkMode={darkMode}>
          <div className={`px-2 pt-1 pb-2 text-xs ${textoFraco}`}>
            <b className={texto}>{menu.aluno.student_name}</b> · {menu.habilidade.codigo} · {bimestre}º bimestre
          </div>
          {legenda.map((l) => {
            const atual = conceitoDe(menu.aluno.id_student, menu.habilidade.id) === l.conceito;
            return (
              <button
                key={l.conceito}
                type="button"
                onClick={() => { marcar(menu.aluno.id_student, menu.habilidade.id, bimestre, l.conceito); setMenu(null); }}
                className={`w-full flex items-start gap-2.5 text-left rounded-lg px-2 py-1.5 text-xs ${atual
                  ? (darkMode ? 'bg-slate-700' : 'bg-slate-100')
                  : (darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50')
                }`}
              >
                <Pilula conceito={l.conceito} />
                <span><b>{l.nome}</b><br /><span className={textoFraco}>{l.descricao}</span></span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => { marcar(menu.aluno.id_student, menu.habilidade.id, bimestre, null); setMenu(null); }}
            className={`w-full text-center rounded-lg py-1.5 mt-1 text-xs ${textoFraco} ${darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}
          >
            Limpar
          </button>
        </Popover>
      )}

      {balao && (
        <Popover ancora={balao.ancora} onFechar={() => setBalao(null)} darkMode={darkMode} largura={320}>
          <div className="p-2 text-sm leading-snug">
            <div className="text-xs mb-1">
              <b className="text-[#3B9AE1]">{balao.habilidade.codigo}</b>
              <span className={textoFraco}> · {balao.habilidade.componente || nomeComponente} · {dados?.serie}</span>
            </div>
            {balao.habilidade.descricao}
            {podeLancar && (
              <div className={`mt-3 pt-2 border-t ${borda}`}>
                <div className={`text-xs mb-1.5 ${textoFraco}`}>
                  Preencher os alunos sem conceito no {bimestre}º bimestre com:
                </div>
                <div className="flex gap-1.5">
                  {CONCEITOS.map((c) => (
                    <button key={c} type="button" onClick={() => preencherColuna(balao.habilidade, c)}
                      className="flex-1 rounded-md py-1 text-xs font-bold text-white hover:opacity-90"
                      style={{ background: COR_DO_CONCEITO[c] }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Popover>
      )}
    </div>
  );
}

export default RegistroHabilidades;
